/**
 * Stale-deploy chunk recovery.
 *
 * THE FAILURE (observed in production 2026-07-26, blank Account page):
 * A browser still running the previous build requests a lazy route chunk whose
 * content hash no longer exists after a deploy. Netlify/CF Pages answer the
 * missing file with the SPA fallback — `index.html`, HTTP 200,
 * `content-type: text/html` — so webpack tries to execute HTML as JavaScript:
 *
 *   ChunkLoadError: Loading chunk 7 failed.
 *     (missing: https://dashboard.kegtron.com/route-account.chunk.fd45f.esm.js)
 *   Uncaught SyntaxError: Unexpected token '<'
 *
 * The route never mounts and the user sees a blank page. It affects only the
 * routes they had not already cached, which is why it can look like "one page
 * is broken" rather than "the app is stale".
 *
 * THE FIX: treat this specific error as "your app is out of date" and reload
 * once to pick up the current shell. Guarded to a single attempt per session —
 * if a reload does not resolve it, we must let the error surface rather than
 * trap the user in a reload loop (see CLAUDE.md "Deploying browser-persisted
 * changes": a bad deploy must self-heal, never wedge).
 *
 * This complements versionCheckService (which politely prompts on a new
 * deploy). This path is the hard failure that needs no prompt: the page is
 * already broken, so reloading strictly improves matters.
 */

export const RECOVERY_FLAG = 'kegtron_chunk_recovery_attempted';

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  // HTML served where JS was expected — the SPA-fallback symptom above.
  /Unexpected token '</,
  /Unexpected token </,
];

/**
 * True when an error indicates a missing/stale webpack chunk rather than an
 * ordinary application bug. Deliberately narrow: a false positive would reload
 * the app out from under a user for an unrelated exception.
 *
 * @param {Error|string|null|undefined} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (typeof error === 'string') {
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(error));
  }
  if (error.name === 'ChunkLoadError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * One recovery attempt per browsing session. Returns true if a reload should
 * be performed now, and records the attempt.
 *
 * sessionStorage (not localStorage) on purpose: a new tab or a later session
 * should be free to try again, but a single broken session must not loop.
 *
 * @returns {boolean}
 */
export function shouldAttemptRecovery() {
  try {
    if (window.sessionStorage.getItem(RECOVERY_FLAG)) return false;
    window.sessionStorage.setItem(RECOVERY_FLAG, String(Date.now()));
    return true;
  } catch {
    // Private mode / storage disabled: allow the reload but we cannot guard
    // against a loop, so only the once-per-page-load nature of the handler
    // protects us. Better than never recovering.
    return true;
  }
}

/**
 * Force the browser to fetch a fresh shell. Any registered service worker is
 * updated first, because the stale precache is usually what is serving the old
 * bundle in the first place.
 */
function recover() {
  const reload = () => window.location.reload(true);
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.update().catch(() => {}))))
        .catch(() => {})
        .then(reload, reload);
      return;
    }
  } catch {
    // fall through to a plain reload
  }
  reload();
}

/**
 * Install global handlers. Safe to call once at startup; a no-op outside a
 * browser.
 */
export function installChunkErrorRecovery() {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event && event.reason) && shouldAttemptRecovery()) recover();
  });

  window.addEventListener('error', (event) => {
    const err = event && (event.error || event.message);
    if (isChunkLoadError(err) && shouldAttemptRecovery()) recover();
  });
}

export default installChunkErrorRecovery;
